import {
  MassText,
  MassTextAttachment,
  Event,
  SubSelectionType,
  Campaign,
  AccountData,
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
  TextPluginOptions,
  DEFAULT_LINK_SUBMISSION_TEXTS_CAMPAIGN_ID,
  MassTextType,
  DEFAULT_PRODUCT_TEXTS_CAMPAIGN_ID,
} from "@markit/common.types";
import {
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "../firebase";
import {
  getMassTextAttachmentsSnap,
  getMassTextsRef,
  getTextRecipientSnap,
  getTextRecipientsRef,
} from "./FirebaseUtils";
import { API } from "../API";
import {
  COMPLIANCE_MESSAGE_DEFAULT,
  detectedLinkShortener,
  isExternalGenericLink,
  isPresetNoRecipientsAutomations,
  isProductSpecificType,
  LessThanDate,
  massTextPlaceholder,
  massTextUpcomingEventPlaceholder,
  ONE_MINUTE_IN_MS,
  retrieveNonGsmCharacters,
} from "@markit/common.utils";
import {
  fetchUniqueEventTickets,
  getTotalUniqueAttendees,
  getUidsFromEventTicketType,
} from "./eventUtils/eventUtils";
import { GetLongDate } from "./GetLongDate";

// Returns the compliance message that gets appended to end of message for non compliant recipients
export const appendedComplianceMessage = `\n\n${COMPLIANCE_MESSAGE_DEFAULT}`;

// For specific users, they have a custom mps because of their brand or their trust score
export const customPhoneMps = (userId: string) => {
  return userId === "qQTEq6dkLtPe1HfwPCYpnS9R9h13" ||
    userId === "q1gd9G5U2BdS4jtmL191MHqU9Ap1" ||
    userId === "G3J2bdndrkQISdGz5dbqQJgUYxE2" ||
    userId === "ey4yGFiECyTKqqcyAahFH1TiMMB3" // thecomedystore, comedystorelj, innovate_bu, rhobb
    ? 10
    : userId === "cIDmS5PbrqeNEBQ0cRaD6QmGLbJ2" // Jonathan
      ? 1
      : undefined;
};

/**
 * Get the mass text attachments from a specified mass text
 */
export const getMassTextAttachments = async (
  userId: string,
  massText: MassText
): Promise<MassTextAttachment[]> => {
  const massTextAttachmentsSnap = await getMassTextAttachmentsSnap(
    userId,
    massText.campaignId,
    massText.id
  );
  const massTextAttachments = massTextAttachmentsSnap.docs.map((doc) =>
    doc.data()
  );
  return massTextAttachments;
};

// get the number of texts by a user was sent to one specified user
export const getNumMassTextsToUser = async (
  userId: string,
  recipientId: string
): Promise<number> => {
  const massTextsRef = getMassTextsRef();
  const massTextsQuery = query(massTextsRef, where("sentBy", "==", userId));
  const massTextsSnap = await getDocs(massTextsQuery);
  const numRecipientsArr: number[] = await Promise.all(
    massTextsSnap.docs.map(async (doc) => {
      const massText = doc.data();
      if (massText.campaignId) {
        const recipientsSnap = await getTextRecipientSnap(
          userId,
          massText.campaignId,
          massText.id,
          recipientId
        );
        return recipientsSnap.exists() ? 1 : 0;
      }
      return 0;
    })
  );
  const totalEventTexts = numRecipientsArr.reduce(
    (acc, count) => acc + count,
    0
  );
  return totalEventTexts;
};

/**
 * Get total number of recipients from a specified mass text (trigger and normal campaigns)
 */
export const getNumMassTextRecipients = async (
  userId: string,
  campaign: Campaign,
  massTextId: string
): Promise<number> => {
  if (campaign.eventId && campaign.initialTextId === massTextId) {
    // Event Automation Initial Trigger Text
    const uniqueAttendees = await getTotalUniqueAttendees(campaign.eventId);
    return uniqueAttendees;
  } else {
    const massTextRecipientsRef = getTextRecipientsRef(
      userId,
      campaign.id,
      massTextId
    );
    const snapshot = await getCountFromServer(massTextRecipientsRef);
    return snapshot.data().count;
  }
};

/**
 * Get the text recipients from a specified mass text
 */
export const getMassTextRecipients = async (
  userId: string,
  campaign: Campaign,
  massTextId: string
): Promise<string[]> => {
  if (campaign.eventId && campaign.initialTextId === massTextId) {
    // Event Automation Initial Trigger Text (backsupport since we didn't track text recipients for event automations initially)
    const tickets = await fetchUniqueEventTickets(campaign.eventId);
    const userIds = tickets.map((ticket) => ticket.redeemedBy);
    return userIds;
  } else {
    const { textRecipients } = await API.text.getTextRecipients({
      userId: userId,
      campaignId: campaign.id,
      massTextId: massTextId,
    });
    return textRecipients;
  }
};

// Returns the user's most recent immediate mass text if it was sent within
// the given window (default 15 min), else null.
export const getRecentlySentMassText = async (
  userId: string,
  withinMinutes: number = 15
): Promise<MassText | null> => {
  const massTextsRef = getMassTextsRef();
  const massTextsQuery = query(
    massTextsRef,
    where("sentBy", "==", userId),
    where("scheduled", "==", false),
    where("type", "==", MassTextType.MASSTEXT),
    orderBy("sentAt", "desc"),
    limit(1)
  );
  const massTextsSnap = await getDocs(massTextsQuery);
  if (!massTextsSnap.empty) {
    const massText = massTextsSnap.docs[0].data();
    if (
      LessThanDate(
        new Date(
          new Date().getTime() - withinMinutes * ONE_MINUTE_IN_MS
        ).toISOString(),
        massText.sentAt
      )
    ) {
      return massText;
    }
  }
  return null;
};

export const checkValidTextMessage = (
  message: string,
  numCredits: number
): { valid: boolean; alertHeading: string; alertSubHeading: string } => {
  // if message is not set
  if (message === "") {
    return {
      valid: false,
      alertHeading: "Write your message first!",
      alertSubHeading:
        "Select recipients and write your message before sending.",
    };
  }
  // if message length exceeds max count
  if (message.length > 1500) {
    return {
      valid: false,
      alertHeading: "Exceeded max character count of 1500.",
      alertSubHeading: "Shorten your message before trying to send again.",
    };
  }
  if (numCredits >= 10) {
    return {
      valid: false,
      alertHeading: "Reached max credit count of 10.",
      alertSubHeading:
        "Shorten your message, split into two texts, or remove emojis or special characters before trying to send again. This is a cap enforced by phone carriers. " +
        `${
          retrieveNonGsmCharacters(message).length > 0
            ? `Detected: ${retrieveNonGsmCharacters(message).join(", ")}`
            : ""
        }`,
    };
  }
  if (detectedLinkShortener(message)) {
    return {
      valid: false,
      alertHeading: "Your message includes a known url link shortener.",
      alertSubHeading: "Remove them before trying to send again.",
    };
  }
  return {
    valid: true,
    alertHeading: "",
    alertSubHeading: "",
  };
};

export const getMassTextSendRate = async (
  massText: MassText,
  numRecipients: number
): Promise<number> => {
  // Send rate early return 100 if numRecipients is 0
  if (numRecipients === 0) {
    return 100;
  }

  const response = await API.tracking.getMassTextSendRate({
    massText: massText,
  });

  if (response) {
    return response.sendRate;
  }

  return 100;
};

// fetch the attendees per ticket type and store the recipients that belong within each ticket type in a map
// Used for mass text selecting by event ticket types
export const fetchAttendeesPerTypeMap = async (selectedEvent: Event) => {
  if (selectedEvent) {
    const attendeesPerTicketTypeArray = await Promise.all(
      selectedEvent.customTickets.map(async (ticket) => {
        const ticketAttendees = await getUidsFromEventTicketType(
          selectedEvent.id,
          ticket.id
        );
        return {
          id: ticket.id,
          attendees: ticketAttendees,
        };
      })
    );
    const attendeesPerTicketType = new Map(
      attendeesPerTicketTypeArray.map((ticketItem) => [
        ticketItem.id,
        ticketItem.attendees,
      ])
    );
    return attendeesPerTicketType;
  }
  return new Map<string, string[]>();
};

// Returns the default message we want to start the message with based on the suggestedActionType
export const massTextDefaultMessage = (
  event: Event | undefined,
  userData: AccountData,
  suggestedActionType: SubSelectionType | undefined
) => {
  return event && suggestedActionType === SubSelectionType.ALL_FOLLOWERS
    ? massTextUpcomingEventPlaceholder(
        userData.fullName,
        GetLongDate(event.start, false, true, true, event.timezone),
        event.id,
        userData.trustedSender
      )
    : massTextPlaceholder(userData.fullName);
};

/**
 * Get the number of recipients that were sent compliance in mass text
 */
export const numRecipientsSentCompliance = async (
  uid: string,
  campaignId: string,
  massTextId: string,
  massTextType: MassTextType
) => {
  // Ignore complianceAdded field for trigger texts
  if (massTextType === MassTextType.TRIGGERTEXT) {
    return 0;
  }
  const textRecipientsRef = getTextRecipientsRef(uid, campaignId, massTextId);
  const query_ = query(textRecipientsRef, where("complianceAdded", "==", true));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Gets the visible plugin options to show based on the eventType and the campaignId
export const getVisibleTextPluginOptions = (
  event: Event | undefined,
  campaignId: string
) => {
  if (isPresetNoRecipientsAutomations(campaignId)) {
    return [];
  }
  let excludePlugins: string[] = [];
  if (event) {
    const excluded = isProductSpecificType(event.eventType)
      ? ["/Event", "Form", "/Calendar"]
      : isExternalGenericLink(event.eventType)
        ? ["/Event", "Product", "/Order", "/Calendar"]
        : ["/Form", "Product"];
    excludePlugins.push(...excluded);
  } else if (campaignId === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID) {
    excludePlugins.push("/Form", "/Product");
  } else if (campaignId === DEFAULT_LINK_SUBMISSION_TEXTS_CAMPAIGN_ID) {
    excludePlugins.push("/Event", "/Product", "/Order", "/Calendar");
  } else if (campaignId === DEFAULT_PRODUCT_TEXTS_CAMPAIGN_ID) {
    excludePlugins.push("/Form", "/Event", "/Calendar");
  } else {
    excludePlugins = ["/Event", "/Form", "/Product", "/Order", "/Calendar"];
  }

  return TextPluginOptions.filter(
    (plugin) => !excludePlugins.some((term) => plugin.name.includes(term))
  );
};
